import { useEffect, useState, useContext, createContext } from "react";
import {
  useLocation,
  useParams,
  useOutletContext,
  useNavigate,
} from "react-router-dom";
import httpService from "V2.0/services/httpService";
import { ReactFlowProvider, useReactFlow } from "@xyflow/react";
import { WorkflowBuilders } from "./Builder/index2";
import { message } from "antd";
import { Button } from "V2.0/common/button";
import { Switch } from "V2.0/common/switch";
import {
  Dialog,
  DialogBody,
  DialogTitle,
  DialogDescription,
  DialogActions,
} from "V2.0/components/DialogFullWidth";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { getPipeline, updatePipeline } from "./service";
import { showErrorToast } from "V2.0/utils";

const BuilderContext = createContext(null);
export const useBuilder = () => {
  return useContext(BuilderContext);
};

function BuilderProvider({ value, children }) {
  return (
    <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>
  );
}

function BuilderHeader(props) {
  const { workflow, onSaveWorkflow } = props;
  const { notify, notifyChange } = useBuilder();
  const { getNodes, getEdges } = useReactFlow();
  const [isActive, setIsActive] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [saveConfirmation, setSaveConfirmation] = useState(false);

  useEffect(() => {
    if (workflow) {
      if (workflow.isActive) {
        onSaveWorkflow({ isActive: false });
      }
    }
  }, [workflow]);

  const transformNodes = (nodes) => {
    let transformedNodes = [];

    for (let node of nodes) {
      const { type, measured, data, id, position } = node;
      const { onDelete, isStartNode, status, ...restData } = data;
      if (type === "custom") {
        const newNode = {
          nodeId: id,
          ...restData,
        };
        transformedNodes.push(newNode);
      }
    }

    return transformedNodes;
  };

  const transformEdges = (edges) => {
    let transformedEdges = [];

    for (let edge of edges) {
      const { type, id, source, target, data } = edge;
      const { output, value } = data;

      const newEdge = {
        from: source,
        to: target,
        condition: `${output}=${value}`,
      };

      transformedEdges.push(newEdge);
    }

    return transformedEdges;
  };

  const onSave = () => {
    const nodes = getNodes();
    const edges = getEdges();

    const transformedNodes = transformNodes(nodes);
    const transformedEdges = transformEdges(edges);

    onSaveWorkflow({
      nodes: transformedNodes,
      edges: transformedEdges,
    });

    setSaveConfirmation(false);
  };

  return (
    <>
      <div className="z-10 w-full border-b p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-x-2">
            <Button
              onClick={() => {
                navigate({pathname: `/pipelines/${workflow._id}`, search: location.search});
              }}
              className="flex-none"
              plain
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
            <p className="text-sm font-semibold ">{workflow?.name || ""}</p>
          </div>
          <div className="flex items-center gap-x-4">
            <div>
              <span className="text-gray-400 text-sm mr-2">Inactive</span>
              <Switch
                checked={isActive}
                onChange={() => {
                  setIsActive((prevState) => {
                    // onSaveWorkflow({ isActive: !prevState });
                    return !prevState;
                  });
                  notifyChange();
                }}
                name="test"
                color="green"
              />
              <span className="text-gray-400 text-sm ml-2">Active</span>
            </div>
            <Button
              onClick={() => setSaveConfirmation(true)}
              disabled={!notify}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
      {saveConfirmation && (
        <Dialog
          open={saveConfirmation}
          onClose={() => setSaveConfirmation(false)}
        >
          <DialogTitle>
            <div className="m-4">Update Pipeline?</div>
          </DialogTitle>
          <DialogDescription>
            <div className="m-4">
              Saving this will cancel all the in progress items. Are you sure
              you want to save changes?
            </div>
          </DialogDescription>
          <DialogActions>
            <div className="flex justify-end m-4 gap-x-2">
              <Button outline onClick={() => setSaveConfirmation(false)}>
                Cancel
              </Button>
              <Button onClick={onSave}>Save</Button>
            </div>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export function Builder(props) {
  const { projectId } = useOutletContext();
  const [workflow, setWorkflow] = useState(null);
  const [notify, setNotify] = useState(false);
  const location = useLocation();
  const { id } = useParams();

  const fetchWorkflow = async () => {
    try {
      const response = await getPipeline({
        workspaceId: projectId,
        pipelineId: id,
      });
      const data = response.data;
      setWorkflow(data);
    } catch (e) {
      showErrorToast(e, "Failed to get pipeline");
    }
  };

  const handleSaveWorkflow = async (data) => {
    message.destroy();
    try {
      const response = await updatePipeline({
        workspaceId: projectId,
        pipelineId: id,
        payload: data,
      });

      if (response.status === 200) {
        message.success("Saved");
      }
    } catch (e) {
      showErrorToast(e, "Failed to save changes");
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchWorkflow();
    }
  }, [projectId]);

  const transformNodes = (nodes) => {
    let transformedNodes = [];

    for (let node of nodes) {
      const { type, measured, data, id, position } = node;
      const { onDelete, isStartNode, status, ...restData } = data;
      if (type === "custom") {
        const newNode = {
          nodeId: id,
          ...restData,
        };
        transformedNodes.push(newNode);
      }
    }

    return transformedNodes;
  };

  const transformEdges = (edges) => {
    let transformedEdges = [];

    for (let edge of edges) {
      const { type, id, source, target, data } = edge;
      const { output, value } = data;

      const newEdge = {
        from: source,
        to: target,
        condition: `${output}=${value}`,
      };

      transformedEdges.push(newEdge);
    }

    return transformedEdges;
  };

  return (
    <ReactFlowProvider>
      <BuilderProvider
        value={{
          saveWorkflow: handleSaveWorkflow,
          notify,
          notifyChange: () => setNotify(true),
          transformNodes,
          transformEdges,
        }}
      >
        {/* <Dialog size="full" open={true} onClose={() => {}}>
          <DialogTitle> */}
        <div className="w-full h-[calc(100vh-80px)] max-h-[calc(100vh-80px)]">
          <BuilderHeader
            workflow={workflow}
            onSaveWorkflow={handleSaveWorkflow}
          />
          {/* </DialogTitle> */}
          {/* <DialogBody className="w-full h-[calc(100vh-90px)]"> */}
          {workflow && (
            <WorkflowBuilders
              onSave={handleSaveWorkflow}
              workflow={workflow}
              projectId={projectId}
            />
          )}
          {/* </DialogBody> */}
          {/* <DialogActions className="w-full h-[40px] border bg-gray-200">
            <div></div>
          </DialogActions> */}
          {/* </Dialog> */}
        </div>
      </BuilderProvider>
    </ReactFlowProvider>
  );
}
